import { LazyLoader } from "@NextSkyTheme/lazy-load";

const newCustomerOfferSelector = ".new-customer-offer__details";
const newCustomerOfferSummarySelector = `${newCustomerOfferSelector} > summary`;
const newCustomerOfferAnimationState = new WeakMap();
const newCustomerOfferAnimationFallback = 220;

function prefersReducedOfferMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function setNewCustomerOfferExpanded(details, expanded) {
  details
    .querySelector(":scope > summary")
    ?.setAttribute("aria-expanded", expanded ? "true" : "false");
}

function clearNewCustomerOfferAnimation(details) {
  const state = newCustomerOfferAnimationState.get(details);
  if (!state) return;

  if (state.frame) cancelAnimationFrame(state.frame);
  if (state.startTimer) clearTimeout(state.startTimer);
  if (state.timer) clearTimeout(state.timer);
  newCustomerOfferAnimationState.delete(details);
}

function finishOpeningNewCustomerOffer(details) {
  clearNewCustomerOfferAnimation(details);
  if (!details.open || details.classList.contains("is-closing")) return;

  details.classList.remove("is-opening");
  details.classList.add("is-visible");
}

function finishClosingNewCustomerOffer(details) {
  clearNewCustomerOfferAnimation(details);
  details.open = false;
  details.classList.remove("is-opening", "is-visible", "is-closing");
  setNewCustomerOfferExpanded(details, false);
}

function openNewCustomerOffer(details) {
  clearNewCustomerOfferAnimation(details);
  details.open = true;
  details.classList.remove("is-closing", "is-visible");
  details.classList.add("is-opening");
  setNewCustomerOfferExpanded(details, true);

  if (prefersReducedOfferMotion()) {
    details.classList.remove("is-opening");
    details.classList.add("is-visible");
    return;
  }

  const popover = details.querySelector(".new-customer-offer__popover");
  if (!popover) {
    finishOpeningNewCustomerOffer(details);
    return;
  }

  popover.getBoundingClientRect();
  let hasStarted = false;
  const startTransition = () => {
    if (hasStarted) return;
    hasStarted = true;

    const state = newCustomerOfferAnimationState.get(details);
    if (state?.frame) cancelAnimationFrame(state.frame);
    if (state?.startTimer) clearTimeout(state.startTimer);
    if (!details.open || details.classList.contains("is-closing")) return;

    details.classList.add("is-visible");
    const timer = setTimeout(
      () => finishOpeningNewCustomerOffer(details),
      newCustomerOfferAnimationFallback,
    );
    newCustomerOfferAnimationState.set(details, { timer });
  };
  const frame = requestAnimationFrame(startTransition);
  const startTimer = setTimeout(startTransition, 34);
  newCustomerOfferAnimationState.set(details, { frame, startTimer });
}

function closeNewCustomerOffer(details) {
  if (!details.open) {
    finishClosingNewCustomerOffer(details);
    return;
  }
  if (details.classList.contains("is-closing")) return;

  clearNewCustomerOfferAnimation(details);
  details.classList.remove("is-opening", "is-visible");
  details.classList.add("is-closing");
  setNewCustomerOfferExpanded(details, false);

  if (prefersReducedOfferMotion()) {
    finishClosingNewCustomerOffer(details);
    return;
  }

  const timer = setTimeout(
    () => finishClosingNewCustomerOffer(details),
    newCustomerOfferAnimationFallback,
  );
  newCustomerOfferAnimationState.set(details, { timer });
}

document.addEventListener("click", (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const summary = target?.closest(newCustomerOfferSummarySelector);
  const currentDetails = target?.closest(newCustomerOfferSelector);

  if (summary && currentDetails) {
    event.preventDefault();

    document
      .querySelectorAll(`${newCustomerOfferSelector}[open]`)
      .forEach((details) => {
        if (details !== currentDetails) closeNewCustomerOffer(details);
      });

    if (
      !currentDetails.open ||
      currentDetails.classList.contains("is-closing")
    ) {
      openNewCustomerOffer(currentDetails);
    } else {
      closeNewCustomerOffer(currentDetails);
    }
    return;
  }

  if (currentDetails) return;

  document
    .querySelectorAll(`${newCustomerOfferSelector}[open]`)
    .forEach((details) => closeNewCustomerOffer(details));
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;

  const openDetails = document.querySelectorAll(
    `${newCustomerOfferSelector}[open]`,
  );
  if (!openDetails.length) return;

  const lastOpenDetails = openDetails[openDetails.length - 1];
  openDetails.forEach((details) => closeNewCustomerOffer(details));
  lastOpenDetails.querySelector(":scope > summary")?.focus();
});

document.addEventListener("transitionend", (event) => {
  if (
    !(event.target instanceof Element) ||
    !event.target.matches(".new-customer-offer__popover") ||
    event.propertyName !== "opacity"
  ) {
    return;
  }

  const details = event.target.closest(newCustomerOfferSelector);
  if (!details) return;

  if (details.classList.contains("is-closing")) {
    finishClosingNewCustomerOffer(details);
  } else if (
    details.classList.contains("is-opening") &&
    details.classList.contains("is-visible")
  ) {
    finishOpeningNewCustomerOffer(details);
  }
});

document.addEventListener(
  "toggle",
  (event) => {
    if (!event.target.matches(newCustomerOfferSelector)) return;

    setNewCustomerOfferExpanded(
      event.target,
      event.target.open && !event.target.classList.contains("is-closing"),
    );
  },
  true,
);

document.addEventListener("DOMContentLoaded", function () {
  function addProductEntry(productJson, storedProducts) {
    if (storedProducts === null) storedProducts = [];
    const currentProductID = productJson.toString();
    if (!storedProducts.includes(currentProductID)) {
      storedProducts.unshift(currentProductID);
      if (storedProducts.length > 25) {
        storedProducts.pop();
      }
      localStorage.setItem(
        "recently-viewed-products",
        JSON.stringify(storedProducts)
      );
    } else {
      const index = storedProducts.indexOf(currentProductID);
      if (index > -1) {
        storedProducts.splice(index, 1);
      }
      storedProducts.unshift(currentProductID);
      localStorage.setItem(
        "recently-viewed-products",
        JSON.stringify(storedProducts)
      );
    }
  }

  const prodData = document.querySelector("[data-product-json]");
  if (prodData != null) {
    const productJson = JSON.parse(prodData.innerHTML);
    const storedProducts = JSON.parse(
      localStorage.getItem("recently-viewed-products")
    );
    addProductEntry(productJson, storedProducts);
  }
});

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }
  init() {
    this.connectedCallback();
  }
  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }
          new LazyLoader(".image-lazy-load");
        })
        .finally(() => {})
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}
customElements.define("product-recommendations", ProductRecommendations);

class StickyAddCart extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  init() {
    const _this = this;
    this.main = document.querySelector(".main-product-section");
    if (!this.main) return;
    const primaryBtn = this.main.querySelector(".block-product__buttons");
    const footer = document.querySelector("footer");
    if (!primaryBtn) return;
    var isVisible = true;
    window.addEventListener("scroll", function () {
      const buttonRect = primaryBtn.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        if (footerRect.top < window.innerHeight - 100) {
          _this.classList.remove("show-sticky-cart");
          document.documentElement.classList.remove("show-sticky-cart-active");
          isVisible = true;
        } else {
          if (
            !isVisible &&
            buttonRect.top < viewportHeight &&
            buttonRect.bottom > 0
          ) {
            isVisible = true;
            _this.classList.remove("show-sticky-cart");
            document.documentElement.classList.remove(
              "show-sticky-cart-active"
            );
          } else if (isVisible && buttonRect.bottom <= 0) {
            isVisible = false;
            _this.classList.add("show-sticky-cart");
            document.documentElement.classList.add("show-sticky-cart-active");
          }
        }
      } else {
        if (
          !isVisible &&
          buttonRect.top < viewportHeight &&
          buttonRect.bottom > 0
        ) {
          isVisible = true;
          _this.classList.remove("show-sticky-cart");
          document.documentElement.classList.remove("show-sticky-cart-active");
        } else if (isVisible && buttonRect.bottom <= 0) {
          isVisible = false;
          _this.classList.add("show-sticky-cart");
          document.documentElement.classList.add("show-sticky-cart-active");
        }
      }
    });
  }
}
customElements.define("sticky-add-cart", StickyAddCart);

const infoGallerySlide = () => {
  const gallery = document.querySelectorAll(".info-gallery__card");
  gallery.forEach((e) => {
    e.addEventListener("mouseover", (event) => {
      const currentTarget = event.currentTarget;
      if (!currentTarget.classList.contains("active")) {
        gallery.forEach((el) => {
          el.classList.remove("active");
        });
        currentTarget.classList.add("active");
      }
    });
  });
};
document.addEventListener("shopify:section:load", function () {
  infoGallerySlide();
});
infoGallerySlide();
